// --- React
import { useCallback, useMemo, useState } from 'react';
// --- Config & Utils
import * as columnDefs from 'datastage/components/AGGrid/config/columns-def';
// --- Hooks
import { useProductsData } from '../../../hooks/useProductsData';

/**
 * Custom hook for products grid utilities.
 *
 * Features:
 * - Manages sorting state and logic for the products grid
 * - Fetches sorted products data from the API via useProductsData
 * - Prepares column definitions with sorting controls
 *
 * @param {object} params - Optional configuration
 * @param {Array} params.columnHandlers - Handlers to pass to column definitions
 * @returns {object} { setSort, columns }
 */
export function useProductsGridUtils({ columnHandlers = [] } = {}) {
    const { fetchProducts, updateProducts, productRange } = useProductsData();
    const [sortedColId, setSortedColId] = useState(null);
    const [sortDirection, setSortDirection] = useState('asc');

    // Handle sorting: update state, fetch sorted data, update products
    const setSort = useCallback(
        async (colId, direction) => {
            setSortedColId(colId);
            setSortDirection(direction);

            const customSort = [colId, direction.toUpperCase()];
            const data = await fetchProducts(productRange, null, customSort);

            if (data) {
                updateProducts({
                    products: data?.content,
                    totalPages: data?.totalPages,
                    totalItems: data?.totalElements,
                    currentPage: data?.pageable?.pageNumber,
                    productSort: customSort,
                });
            }
        },
        [fetchProducts, updateProducts, productRange],
    );

    // Prepare column definitions with sorting controls
    const baseColumns = columnDefs['products'](...columnHandlers)['default'];

    const columns = useMemo(
        () =>
            baseColumns.map((col) => ({
                ...col,
                headerComponentParams: {
                    ...col.headerComponentParams,
                    setSort,
                    sortedColId,
                    sortDirection,
                },
            })),
        [setSort, sortedColId, sortDirection, baseColumns],
    );

    return { setSort, columns };
}
